// CompanySelectorXL - Sprint 44 Fase 3 Vista 7.5.
// Migracion XL del CompanySelector legacy. Mantiene API y logica
// (useTenant) sin cambios; solo reemplaza Tailwind hardcoded por tokens
// XL + dark mode. 3 estados de render preservados:
//   1) Sin empresa -> texto muted.
//   2) Una empresa -> caja con icono + nombre truncate.
//   3) Varias empresas -> XLSelect con dropdown nativo.
// Hover state agregado. SVG icono inline preservado.
import { useEffect } from 'react';
import { useTenant } from '../tenant/TenantContext';
import { ensureKeyframes, injectCSS } from '../design/motion';
import XLSelect from '../components/xl/XLSelect';

const CSS_ID = 'xl-cosel-css';
const CSS = `
.xl-cosel-empty {
  font-size: 12px;
  color: var(--xl-color-text-muted);
  padding: 6px 12px;
  font-family: var(--xl-font-sans);
}

.xl-cosel-static {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid var(--xl-color-border);
  background: var(--xl-color-surface-alt);
  border-radius: var(--xl-radius-md);
  color: var(--xl-color-text);
  font-family: var(--xl-font-sans);
  font-size: 12px;
  font-weight: 500;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1);
  max-width: 240px;
}
.xl-cosel-static:hover {
  background: var(--xl-color-surface);
  border-color: rgba(46, 55, 69, 0.25);
}

.xl-cosel-static__ico {
  color: var(--xl-color-text-muted);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}

.xl-cosel-static__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.xl-cosel-multi {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.xl-cosel-multi__ico {
  color: var(--xl-color-text-muted);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}
.xl-cosel-multi__select {
  min-width: 200px;
}
`;

function BuildingIcon() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
    </svg>
  );
}

export default function CompanySelector() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const t = useTenant();

  // Estado 1: sin empresa
  if (!t.currentCompanyId && t.companies.length === 0) {
    return (
      <div className="xl-cosel-empty" role="note">
        (sin empresa)
      </div>
    );
  }

  const current = t.companies.find((c) => c.id === t.currentCompanyId);

  // Estado 2: una sola empresa -> chip estatico
  if (t.companies.length <= 1) {
    return (
      <div
        className="xl-cosel-static"
        title={current ? current.nombre : ''}
      >
        <span className="xl-cosel-static__ico">
          <BuildingIcon />
        </span>
        <span className="xl-cosel-static__name">
          {current ? current.nombre : '—'}
        </span>
      </div>
    );
  }

  // Estado 3: varias empresas -> dropdown XLSelect
  const options = t.companies.map((c) => ({
    value: String(c.id),
    label: c.nombre,
  }));

  return (
    <div className="xl-cosel-multi">
      <span className="xl-cosel-multi__ico">
        <BuildingIcon />
      </span>
      <span className="xl-cosel-multi__select">
        <XLSelect
          value={t.currentCompanyId == null ? '' : String(t.currentCompanyId)}
          onChange={(e) => t.switchCompany(e.target.value)}
          options={options}
          ariaLabel="Empresa actual"
        />
      </span>
    </div>
  );
}
